/**
 * Member pricing on a package, decided the way purchasePackage decides it on
 * the server (PurchaseService): the client's member-access memberships must
 * cover every location the pack can be spent at. For a pack that books all its
 * sessions at purchase, that is where the picked sessions are; for any other
 * pack, everywhere it can be spent — a shared pack at all of its locations, a
 * per-location pack where it is bought. The server has the last word; this
 * only keeps the price on screen honest.
 *
 * memberStatus is the membership-status endpoint's answer:
 * { hasMembership, memberEverywhere, memberLocationIds }.
 */

const covers = (memberStatus, locationIds) => {
  if (!memberStatus?.hasMembership) return false;
  if (memberStatus.memberEverywhere) return true;
  const mine = memberStatus.memberLocationIds || [];
  return locationIds.length > 0 && locationIds.every((id) => mine.includes(id));
};

// Where a pack bought now can be spent, before any session is picked.
export const packSpendLocationIds = (pkg, purchaseLocationId = null) =>
  pkg?.crossLocationMode !== "shared" && purchaseLocationId ? [purchaseLocationId] : pkg?.locationIds || [];

/**
 * { price, regularPrice, memberPrice, applies, reachAt, offBranch }
 * - price: what the purchase will charge.
 * - regularPrice: the non-member price, shown struck through when price is below it.
 * - applies: that price is the member price.
 * - reachAt: when the member price turns on WHERE — the sessions a book-all
 *   pack picks, or the branch a per-location pack is still to be bought at —
 *   the pack's locations the client is a member at. Empty otherwise.
 * - offBranch: book-all picks at a branch the membership doesn't reach — each
 *   one is what keeps the member price off.
 */
export function packMemberPricing({ pkg, opt, memberStatus, purchaseLocationId = null, sessionLocationIds = [] }) {
  const regular = parseFloat(opt?.price) || 0;
  const member = parseFloat(opt?.memberPrice) || 0;
  const off = { price: regular, regularPrice: regular, memberPrice: member, applies: false, reachAt: [], offBranch: 0 };
  if (!(member > 0 && member < regular) || !memberStatus?.hasMembership) return off;

  const spendIds = packSpendLocationIds(pkg, purchaseLocationId);
  if (covers(memberStatus, spendIds)) return { price: member, regularPrice: regular, memberPrice: member, applies: true, reachAt: [], offBranch: 0 };

  const bookAll = opt?.upfrontBooking === "all";
  const branchToPick = pkg?.crossLocationMode !== "shared" && !purchaseLocationId;
  if (!bookAll && !branchToPick) return off;

  const mine = memberStatus.memberLocationIds || [];
  const reachAt = (spendIds.length > 0 ? spendIds : mine).filter((id) => mine.includes(id));
  if (reachAt.length === 0) return off;

  const applies = bookAll && covers(memberStatus, sessionLocationIds);
  const offBranch = bookAll && !applies ? sessionLocationIds.filter((id) => !mine.includes(id)).length : 0;
  return { price: applies ? member : regular, regularPrice: regular, memberPrice: member, applies, reachAt, offBranch };
}

/**
 * { text, warning } saying where the member price holds, or null when location
 * doesn't enter into it. warning: the member price isn't on this purchase yet —
 * a condition still to meet, or a pick that sits elsewhere and keeps it off. nameOf resolves a location id to its name.
 * withAmount: false where the line sits under the member price itself (the
 * package row): "Only when every session is at …".
 */
export function memberPriceNote({ pricing, opt, currency, nameOf, withAmount = true }) {
  const names = (pricing?.reachAt || []).map(nameOf).filter(Boolean);
  if (names.length === 0) return null;
  const where = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
  const amount = withAmount ? ` ${`${currency || ""} ${pricing.memberPrice.toFixed(2)}`.trim()}` : "";
  const lead = withAmount ? `Member price${amount}` : "Only";
  if (opt?.upfrontBooking !== "all") return { text: `${lead} when bought at ${where}`, warning: true };
  if (pricing.applies) return { text: `Member price applied — keep every session at ${where}`, warning: false };
  const n = pricing.offBranch || 0;
  if (n > 0) {
    const sessions = n === 1 ? "1 session isn't" : `${n} sessions aren't`;
    return { text: `${sessions} at ${where} — member price${amount} needs every session there`, warning: true };
  }
  return { text: `${lead} when every session is at ${where}`, warning: true };
}
